import { AppState } from '../types/storage';
import { Conversation, Message } from '../types/chat';
import { EventBus } from '../utils/eventBus';

export class StorageService {
  private readonly storageKey = 'asknow_app_state';
  private eventBus: EventBus;

  constructor(eventBus: EventBus) {
    this.eventBus = eventBus;
  }

  getState(): AppState {
    const stored = localStorage.getItem(this.storageKey);
    if (!stored) {
      return this.getDefaultState();
    }

    try {
      const parsed = JSON.parse(stored);
      // Validate version and migrate if needed
      if (parsed.version !== '1.0.0') {
        return this.getDefaultState();
      }
      return parsed;
    } catch {
      return this.getDefaultState();
    }
  }

  private getDefaultState(): AppState {
    return {
      version: '1.0.0',
      conversations: {},
      activeConversationId: null,
      settings: {
        defaultModel: 'gpt-4o-mini',
        theme: 'light',
      },
    };
  }

  saveState(state: AppState): void {
    localStorage.setItem(this.storageKey, JSON.stringify(state));
    this.eventBus.emit('state:updated', state);
  }

  createConversation(model: string): Conversation {
    const state = this.getState();
    const conversation: Conversation = {
      id: `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: 'New Conversation',
      messages: [],
      model,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };

    state.conversations[conversation.id] = conversation;
    state.activeConversationId = conversation.id;
    this.saveState(state);
    this.eventBus.emit('conversation:created', conversation);
    this.eventBus.emit('activeConversation:changed', conversation.id);
    this.eventBus.emit('conversation:updated', conversation);

    return conversation;
  }

  getConversation(id: string): Conversation | null {
    const state = this.getState();
    return state.conversations[id] || null;
  }

  updateConversation(id: string, updates: Partial<Conversation>): void {
    const state = this.getState();
    const conversation = state.conversations[id];

    if (!conversation) {
      throw new Error(`Conversation ${id} not found`);
    }

    Object.assign(conversation, updates);
    conversation.updatedAt = Date.now();
    this.saveState(state);
    this.eventBus.emit('conversation:updated', conversation);
  }

  deleteConversation(id: string): void {
    const state = this.getState();
    delete state.conversations[id];

    // If deleted conversation was active, switch to another or null
    if (state.activeConversationId === id) {
      const conversationIds = Object.keys(state.conversations);
      state.activeConversationId = conversationIds.length > 0 ? conversationIds[0] : null;
    }

    this.saveState(state);
    this.eventBus.emit('conversation:deleted', id);
  }

  resetConversation(id: string): void {
    const state = this.getState();
    const conversation = state.conversations[id];

    if (!conversation) {
      throw new Error(`Conversation ${id} not found`);
    }

    conversation.messages = [];
    conversation.title = 'New Conversation';
    conversation.createdAt = Date.now();
    conversation.updatedAt = Date.now();

    this.saveState(state);
    this.eventBus.emit('conversation:updated', conversation);
  }

  addMessage(conversationId: string, message: Message): void {
    const state = this.getState();
    const conversation = state.conversations[conversationId];

    if (!conversation) {
      throw new Error(`Conversation ${conversationId} not found`);
    }

    conversation.messages.push(message);
    conversation.updatedAt = Date.now();

    // Auto-generate title from first user message
    if (conversation.messages.length === 1 && message.role === 'user') {
      const content = typeof message.content === 'string'
        ? message.content
        : (message.content.find(c => c.type === 'text')?.text || 'New Conversation');
      conversation.title = content.slice(0, 50) + (content.length > 50 ? '...' : '');
    }

    this.saveState(state);
    this.eventBus.emit('conversation:updated', conversation);
  }

  updateMessage(conversationId: string, messageId: string, content: string): void {
    const state = this.getState();
    const conversation = state.conversations[conversationId];

    if (!conversation) {
      throw new Error(`Conversation ${conversationId} not found`);
    }

    const message = conversation.messages.find(m => m.id === messageId);
    if (message) {
      message.content = content;
      conversation.updatedAt = Date.now();
      this.saveState(state);
      this.eventBus.emit('conversation:updated', conversation);
    }
  }

  getMessages(conversationId: string): Message[] {
    const conversation = this.getConversation(conversationId);
    return conversation ? conversation.messages : [];
  }

  getActiveConversation(): Conversation | null {
    const state = this.getState();
    if (!state.activeConversationId) {
      return null;
    }
    return state.conversations[state.activeConversationId] || null;
  }

  setActiveConversation(id: string): void {
    const state = this.getState();
    if (!state.conversations[id]) {
      throw new Error(`Conversation ${id} not found`);
    }
    state.activeConversationId = id;
    this.saveState(state);
    this.eventBus.emit('activeConversation:changed', id);
  }

  getAllConversations(): Conversation[] {
    const state = this.getState();
    return Object.values(state.conversations).sort((a, b) => b.updatedAt - a.updatedAt);
  }

  updateSettings(settings: Partial<AppState['settings']>): void {
    const state = this.getState();
    Object.assign(state.settings, settings);
    this.saveState(state);
    this.eventBus.emit('settings:updated', state.settings);
  }

  getSettings(): AppState['settings'] {
    return this.getState().settings;
  }
}
